import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

let openModalCount = 0;
let previousBodyOverflow = '';

// ── types ────────────────────────────────────────────────────

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
}

// ── component ────────────────────────────────────────────────

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 480,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Lock body scroll & handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    if (openModalCount === 0) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openModalCount += 1;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
      }
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      style={styles.backdrop}
      onMouseDown={e => {
        const target = e.target as Node;
        // Ignore clicks inside the panel or any portaled overlay (e.g. player picker dropdown)
        if (panelRef.current?.contains(target)) return;
        if ((target as Element).closest?.('[data-portal-overlay]')) return;
        onClose();
      }}
    >
      <div ref={panelRef} style={{ ...styles.panel, maxWidth }}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Închide">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Body */}
        <div style={styles.body}>{children}</div>

        {/* Footer */}
        {footer && (
          <>
            <div style={styles.divider} />
            <div style={styles.footer}>{footer}</div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

// ── styles ───────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100dvh',
    height: '100dvh',
    padding: 'calc(1rem + env(safe-area-inset-top)) 1rem calc(1rem + env(safe-area-inset-bottom))',
    background: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    animation: 'modalBackdropIn .18s ease',
  },
  panel: {
    width: '100%',
    background: 'var(--tt-surface)',
    borderRadius: 20,
    boxShadow:
      '0 4px 6px -1px rgba(0,0,0,.07), 0 24px 48px -8px rgba(0,0,0,.22)',
    border: '1px solid var(--tt-border)',
    animation: 'modalPanelIn .2s cubic-bezier(.34,1.56,.64,1)',
    overflow: 'hidden',
    maxHeight: 'calc(100dvh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.5rem',
    flexShrink: 0,
  },
  title: {
    fontWeight: 800,
    fontSize: '1.05rem',
    color: 'var(--tt-text)',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    padding: 0,
    borderRadius: '50%',
    border: '1px solid var(--tt-border)',
    background: 'var(--tt-surface-2)',
    color: 'var(--tt-text-muted)',
    cursor: 'pointer',
    flexShrink: 0,
    lineHeight: 1,
    boxSizing: 'border-box',
    transition: 'background .15s, color .15s',
  },
  divider: {
    height: 1,
    background: 'var(--tt-border)',
  },
  body: {
    padding: '1.5rem',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  footer: {
    padding: '1rem 1.5rem',
    paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
    flexShrink: 0,
  },
};

export default Modal;
